<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import {
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  provide,
  computed,
  shallowRef,
  nextTick,
  useTemplateRef,
} from "vue";
import { until, useLocalStorage, useTitle } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { getActivePinia } from "pinia";
import {
  captureException,
  captureMessage,
  type SeverityLevel,
} from "@sentry/browser";

import { Locator, LocatorLocations } from "@readium/shared";
import { desktopOS, posthogKeys, themeMap } from "@/helpers/constants";
import {
  helperResizeListener,
  RemoveNavigatorOpacity,
  getDebugMode,
} from "@/helpers/helperFunctions";
import TopBar from "./ReaderTitleBar.vue";
import ReaderProgressionBar from "./ReaderProgressionBar.vue";
import SideArrow from "./SideArrow.vue";
import type { WebReaderMessage } from "@/helpers/types";
import ZoomSelector from "@/components/shared/ZoomSelector.vue";
import ZoomCard from "@/components/shared/ZoomCard.vue";
import TooltipText from "@/components/shared/TooltipText.vue";
import TTSPlayer from "@/components/tts/TTSPlayer.vue";
import OfflineReading from "@/services/offline/OfflineService";
import SecondaryMenu from "./SecondaryMenu.vue";
import CursorTool from "../shared/CursorTool.vue";
import { GuidedNavigationService } from "@/services/guidedNavigation/GuidedNavigationService";

// Pinia stores
import { useSettingsStore } from "@/stores/settings";
import { useAttributesStore } from "@/stores/attributes";
import { useUiVisibilityStore } from "@/stores/uiVisibility";
import { usePostHog } from "@/helpers/posthog";
import { useProgressionStore } from "@/stores/progression";

import UILayers, { CloseSource, OverlayType, useUILayers } from "../UILayers";
import InteractiveCtaToastContent from "./InteractiveCtaToastContent.vue";
import { useKeyboardEvent } from "@/services/composables/useKeyboardEvents";
import TTSEngine from "@/services/tts/TTSEngine";
import MediaPlayer, { MediaPlayerState } from "@/services/media/player";
import { Direction, Navigator } from "../navigator/types";
import { provideNavigator } from "../navigator";
import { invokeAsync } from "@/helpers/async";
import { setLocale } from "@/i18n";
import { provideWpsClient, WPSClient } from "@/wps/client";
import { useWpsStore } from "@/stores/wps";

import { OS, UA } from "../../vendor/sML.js";
import LiveAnnouncer from "./LiveAnnouncer.vue";
import { useLiveAnnouncer } from "@/services/composables/useLiveAnnouncer";
import { useNavigatorComponent } from "../navigator/loader";

import { installDevtoolProtection } from "../../../modules/content-protection/devtool";
import { useEmbedModule } from "../../../modules/embed/composable";
import { useEmbedShortcuts } from "../../../modules/embed/shortcuts";
import eventBus from "../../../modules/event-bus";
import DictionaryModal from "./DictionaryModal.vue";
import { useDictionary } from "../navigator/epub/dictionary";

import type { EpubNavigatorContainer } from "../navigator/NavigatorContainer.vue";
import { useMediaQuery } from "@/helpers/useMediaQuery";

const devtoolsOpened = shallowRef(false);
installDevtoolProtection(
  (type: number, seenTypes: number[]) => {
    if (seenTypes.length === 1 && type === 6) {
      // ignore console perf detection for now
      // when it's the only detector triggered
      // as it appears to be too brittle
      return;
    }

    const levels: SeverityLevel[] = ["info", "warning", "error", "fatal"];

    // TODO: don't act for now, just report
    //devtoolsOpened.value = true;
    captureMessage("Devtools opened", {
      level: levels[seenTypes.length - 1] || "fatal",
      extra: {
        detection: type,
        seenTypes,
        url_value: wpsClient.token.raw,
        publication: wpsClient.publication?.metadata?.identifier,
      },
    });
  },
  () => (devtoolsOpened.value = false)
);

const settingsStore = useSettingsStore();
const attributesStore = useAttributesStore();
const visibilityStore = useUiVisibilityStore();
const progressionStore = useProgressionStore();

const { wpsClient } = defineProps<{ wpsClient: WPSClient }>();
provideWpsClient(wpsClient);
useWpsStore().setClient(wpsClient);

const { activeDefinition } = useDictionary();

// Attach keyboard event listeners
useKeyboardEvent(window, {
  onNext: () => goTo("next", true),
  onPrevious: () => goTo("previous", true),
});
useEmbedShortcuts(window);

const NavigatorComponent = useNavigatorComponent();

const guidedNavigationService = shallowRef<GuidedNavigationService>(null);

const mediaPlayer = computed<MediaPlayer | null>(() => {
  if (guidedNavigationService.value) {
    return guidedNavigationService.value;
  }

  if (TTSEngine.state !== MediaPlayerState.Stopped) {
    return TTSEngine;
  }

  return null;
});

provide("mediaPlayer", mediaPlayer);

watch(
  () => TTSEngine.state,
  async (state) => {
    if (state !== MediaPlayerState.Stopped) {
      return;
    }

    navigatorRef.value.clearHighlight("tts-all");
  }
);

const error = ref<string>(undefined);

const isPreferredDark = useMediaQuery("(prefers-color-scheme: dark)");

const effectiveTheme = computed(() => {
  const appearance = settingsStore.global.theme;
  if (appearance === "default") {
    return isPreferredDark.value ? "night" : "day";
  }
  return appearance;
});

// TODO: change those to be on the same ref name
const navigatorRef = useTemplateRef<Navigator>("navRef");
provideNavigator(navigatorRef);

const isPannable = computed(() => navigatorRef.value?.isPannable ?? false);

const { onReady } = useEmbedModule(wpsClient, navigatorRef);

watch(
  effectiveTheme,
  (appearance) => {
    if (!appearance) return;
    const theme = themeMap[appearance];
    Object.keys(theme).forEach((prop) => {
      document.documentElement.style.setProperty(prop, theme[prop]);
    });
    // handle theme background override if exists
    const bgOverride = settingsStore.global.themeOverrides?.bg;
    if (bgOverride)
      document.documentElement.style.setProperty(
        "--DM__theme-bg-color",
        bgOverride
      );
  },
  { immediate: true }
);

watch(
  () => settingsStore.perContent.ttsHighlight,
  (color) =>
    // CSS variable of highlight color
    document.documentElement.style.setProperty(
      "--DM__theme-pdf-highlight-color",
      color
    ),
  { immediate: true }
);

const scrollMode = computed<boolean>(() => !!settingsStore.perContent.scroll);
const fontFamily = computed<string>(() => settingsStore.perContent.fontFamily);

const initialPosition = until(() => progressionStore.ready)
  .toBe(true, {
    timeout: 5000,
  })
  .then(() => progressionStore.lastKnownPosition);

watch(
  () => navigatorRef.value?.activeLocator,
  (locator: Locator) => {
    if (!locator) return;

    progressionStore.pushLocation(locator);
  }
);

const debugMode = ref<boolean>(false);

const { capture: capturePosthogEvent } = usePostHog(() => ({
  publication_title:
    wpsClient.publication?.metadata?.title?.getTranslation("en"),
}));

const { t } = useI18n();
const statusPoliteRef = ref<InstanceType<typeof LiveAnnouncer> | null>(null);

useTitle(
  computed(() => {
    const pubTitle =
      wpsClient.publication?.metadata?.title?.getTranslation("en");
    const appTitle = t("title");
    return pubTitle ? `${pubTitle} | ${appTitle}` : appTitle;
  })
);

async function handlePostMessage(e: MessageEvent) {
  if (e.source === null || typeof e.data !== "object") return;
  if (!readyForMessages) return;
  if (e.data._dmreader !== 1) return;
  const d = e.data as WebReaderMessage;
  let loc: Locator | LocatorLocations;
  switch (d.key) {
    case "go":
      loc = Locator.deserialize(d.data.locator);
      if (!loc) throw new Error("Invalid locator");
      try {
        await navigatorRef.value.go(loc);
        if (e.source !== window) {
          e.source.postMessage({
            _dmreader: 1,
            id: d.id || null,
            key: "went",
          } as WebReaderMessage);
        }
      } catch {
        if (e.source !== window) {
          e.source.postMessage({
            _dmreader: 1,
            id: d.id || null,
            key: "error",
            data: {
              message: "Invalid locator",
            },
          } as WebReaderMessage);
        }
      }
      break;
    case "decorate":
      // TODO
      break;
    default:
      throw new Error("Unknown message key: " + e.data.key);
  }
}

let closeInteractiveCta: () => void = null;
const interactiveCtaOpen = ref<boolean>(false);
const interactiveCtaDismissCount = useLocalStorage("DM__overlay-index", 0);
const { showToast } = useUILayers();

// Initialized helps us to avoid multiple onmounted calls on dev mode
let initialized = false;
onMounted(async () => {
  if (initialized) return;
  initialized = true;
  const { mobileSizeScreen, hoverSizeScreen } = helperResizeListener();
  attributesStore.setMobile(mobileSizeScreen);
  attributesStore.setIphone(
    /iPhone|iPod/i.test(window.navigator.userAgent) && !window.MSStream
  );
  attributesStore.setIpad(
    /iPad/i.test(window.navigator.userAgent) && !window.MSStream
  );

  attributesStore.setScreenHasHover(hoverSizeScreen && !mobileSizeScreen);

  window.addEventListener("resize", resizeListener);

  const isDesktop = desktopOS.some((os) => {
    return Object.hasOwn(OS, os);
  });
  attributesStore.setDesktopOS(isDesktop);

  if (getDebugMode()) {
    debugMode.value = true;
    console.log("Debug mode enabled...");
  }

  await until(() => wpsClient.publication).not.toBeNull();

  if (wpsClient.media.isMediaOverlay && interactiveCtaDismissCount.value <= 2) {
    closeInteractiveCta = showToast(
      InteractiveCtaToastContent,
      {
        onCtaTriggered: () => {
          invokeAsync(() =>
            guidedNavigationService.value?.startAtLocator(
              navigatorRef.value.activeLocator
            )
          );
        },
      },
      "record_voice_over",
      {
        timeout: 7000,
        closeAction: true,
        overlay: OverlayType.Normal,
        onClose: (source) => {
          closeInteractiveCta = null;
          interactiveCtaOpen.value = false;
          // user explicitly dismissed the toast
          // we don't want to show it again after a few times
          if (source === CloseSource.CloseButton) {
            interactiveCtaDismissCount.value++;
          }
        },
      }
    );
    interactiveCtaOpen.value = true;
  }

  window.addEventListener("message", handlePostMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeListener);
  window.removeEventListener("message", handlePostMessage);

  mediaPlayer.value?.stop();

  // map through that list and use the **$reset** fn to reset the state
  // @ts-expect-error private property
  getActivePinia()._s.forEach((store) => store.$reset());
});

async function init() {
  await Promise.all([
    until(() => wpsClient.publication).not.toBeNull(),
    setLocale(wpsClient.token.language || null),
  ]);

  visibilityStore.headless = !wpsClient.token.uiEnabled;
  if (debugMode.value) console.log("wps", wpsClient);
  // Check if the publication is a PDF
  if (wpsClient.media.isPDF) {
    if (wpsClient.token.isSample) {
      capturePosthogEvent(posthogKeys.openPDFSample);
    } else {
      capturePosthogEvent(posthogKeys.openPDF);
    }

    // Minimum version of Safari supported for PDF.JS is 15.4
    if (UA.Safari) {
      if (UA.Safari[0] < 15 || (UA.Safari[0] === 15 && UA.Safari[1] < 4)) {
        alert("Your version of Safari is too old!");
      }
    }
  } else if (wpsClient.media.isFXL) {
    if (wpsClient.token.isSample) {
      capturePosthogEvent(posthogKeys.openFXLSample);
    } else {
      capturePosthogEvent(posthogKeys.openFXL);
    }
  }

  if (!wpsClient.media.isPDF && !wpsClient.media.isFXL) {
    if (wpsClient.token.isSample) {
      capturePosthogEvent(posthogKeys.openEPUBSample);
    } else {
      capturePosthogEvent(posthogKeys.openEPUB);
    }
  }

  if (wpsClient.media.isMediaOverlay && interactiveCtaDismissCount.value <= 2) {
    closeInteractiveCta = showToast(
      InteractiveCtaToastContent,
      {
        onCtaTriggered: () => {
          invokeAsync(async () => {
            const start = await (
              navigatorRef.value as EpubNavigatorContainer
            ).getGuidedNavigationStartingPoint();
            if (start instanceof Locator) {
              await guidedNavigationService.value?.startAtLocator(start);
            } else {
              await guidedNavigationService.value?.startAtElement(
                start.href,
                start.elementID
              );
            }
          });
        },
      },
      "record_voice_over",
      {
        timeout: 20000,
        closeAction: true,
        overlay: OverlayType.Normal,
        onClose: (source) => {
          closeInteractiveCta = null;
          interactiveCtaOpen.value = false;
          // user explicitly dismissed the toast
          // we don't want to show it again after a few times
          if (source === CloseSource.CloseButton) {
            interactiveCtaDismissCount.value++;
          }
        },
      }
    );
    interactiveCtaOpen.value = true;
  }

  // Init the live announcer container
  await nextTick();

  // TODO: this could silently fail to initialize the live announcer
  // if the ref is not yet available
  if (statusPoliteRef.value)
    useLiveAnnouncer.initWithElement(
      "politeStatus",
      statusPoliteRef.value.element
    );

  visibilityStore.setNavigatorReady(true);
}

/* Using the offline service to check if offline is supported */
invokeAsync(async () => {
  if (!OfflineReading.supported) {
    if (UA.Firefox) {
      error.value =
        "🛑 Your browser does not support essential features required run the reader. Try quitting private browsing mode if you are currently using it.";
    } else {
      error.value =
        "🛑 Your browser does not support essential features required run the reader.";
    }
    return;
  }
  try {
    await OfflineReading.init;
    await init();
  } catch (err) {
    captureException(err);
    console.error(err);
    error.value = `⚠️ ${err}`;
  }
});

function handleNavigatorError(err: Error) {
  console.error("Error loading navigator:", err);
  error.value = `⚠️ Navigator ${err}`;
  captureException(err, {
    extra: {
      url_value: wpsClient.token.raw,
      publication: wpsClient.publication?.metadata?.identifier,
    },
  });
}

let readyForMessages = false;
async function ready() {
  setTimeout(() => RemoveNavigatorOpacity(), 600);

  onReady();

  readyForMessages = true;

  if (wpsClient.media.isMediaOverlay) {
    guidedNavigationService.value = new GuidedNavigationService(
      wpsClient.publication
    );
  }
}

async function goTo(
  direction: Direction,
  keepui: boolean = true
): Promise<void> {
  if (mediaPlayer.value) {
    mediaPlayer.value.stop();
  }
  await navigatorRef.value?.go(direction);
  if (!keepui) visibilityStore.setShowUI(false);
}

function resizeListener(): void {
  const { mobileSizeScreen, hoverSizeScreen } = helperResizeListener();
  attributesStore.setMobile(mobileSizeScreen);
  attributesStore.setScreenHasHover(hoverSizeScreen && !mobileSizeScreen);
}

function handleUIToggle(show: boolean) {
  visibilityStore.setShowUI(show);
}

watch(
  () => navigatorRef.value?.activeSelection || null,
  (selection, oldValue) => {
    if (selection === oldValue) return;
    eventBus.emit("navigator:selection", selection);
    if (selection === null) {
      visibilityStore.setShowUI(true);
    } else {
      visibilityStore.setShowUI(false);
    }
  }
);

function textSelected(): boolean {
  return (window.getSelection()?.toString()?.length ?? 0) > 0;
}
</script>

<template>
  <div
    class="wrapper-content fixed w-screen h-screen overflow-hidden font"
    :class="fontFamily ? 'font--' + fontFamily : null"
  >
    <div
      v-if="error"
      class="h-screen flex p-5 justify-center items-center text-red-600"
    >
      <div class="flex flex-wrap justify-center text-center">
        <div class="w-full mb-4 flex justify-center items-start">
          <span class="material-icons mr-1" :aria-label="t('error')"
            >error</span
          >
          <span class="text-center">{{ t("reader.errorMessage") }}</span>
        </div>
        <span class="w-full mb-4">{{ error }}</span>
      </div>
    </div>
    <div v-else-if="!wpsClient.publication">
      <div
        class="loading-container--fade h-screen flex flex-wrap flex-col justify-center items-center"
      >
        <div
          class="spinner-border animate-spin inline-block w-16 h-16 rounded-full text-red-700"
          role="status"
        >
          <span class="invisible">{{ t("reader.loading") }}...</span>
        </div>
        <span class="w-full text-center mt-5 text-red-700 px-5">{{
          t("reader.loadingPublication")
        }}</span>
      </div>
    </div>
    <template v-else>
      <main
        class="reader-container relative w-screen h-screen"
        :class="{
          ['vertical-mode']: scrollMode,
          ['reader-container--tts']: !!mediaPlayer,
          ['reader-container--interactiveMode']: wpsClient.interactive,
        }"
      >
        <Transition v-if="wpsClient.token.uiEnabled" name="fade-shadow">
          <div
            v-if="!visibilityStore.showUI"
            class="topbar--shadow w-full absolute flex justify-center right-0 top-0 pt-5 pb-5"
            :class="[
              wpsClient.media.isPDF
                ? 'topbar--shadow-pdf'
                : wpsClient.media.isFXL
                  ? 'topbar--shadow-fxl'
                  : null,
              wpsClient.interactive ? 'topbar--shadow-interactive' : null,
              attributesStore.screenHasHover ? null : 'topbar--shadow-no-hover',
            ]"
            @click="
              wpsClient.interactive ? null : visibilityStore.setShowUI(true)
            "
          >
            <span
              v-if="!wpsClient.media.isPDF && !scrollMode"
              class="text-center truncate truncate--shadow"
            >
              {{ wpsClient.publication?.metadata.title.getTranslation("en") }}
            </span>
            <button
              :aria-label="'Open ' + t('moreOptions.name')"
              title="More options"
              class="square-icon-button square-icon-button--shadow absolute"
              :class="scrollMode ? 'square-icon-button--shadow-bg' : null"
              @click="handleUIToggle(!visibilityStore.showUI)"
            >
              <span class="material-icons" aria-hidden="true"> more_vert </span>
            </button>
          </div>
        </Transition>

        <div
          v-if="visibilityStore.showUI && wpsClient.interactive"
          class="overlay overlay--transparent"
          @click="visibilityStore.setShowUI(false)"
        ></div>
        <TopBar
          :title="wpsClient.publication?.metadata.title.getTranslation('en')"
          :url-go-back="wpsClient.token.backAffordance?.href"
        />

        <UILayers />

        <LiveAnnouncer
          ref="statusPoliteRef"
          role="status"
          politeness="polite"
        />

        <Transition name="slide-left">
          <TooltipText
            v-if="visibilityStore.showTooltipCopyLink"
            :text="t('tooltip.copiedLink')"
          />
        </Transition>

        <SideArrow
          v-if="wpsClient.token.uiEnabled && !scrollMode"
          side="left"
          :go-to="goTo"
        />

        <article
          :aria-label="
            t('reader.reading') +
            wpsClient.publication.metadata.title.getTranslation('en')
          "
          class="container-full-screen flex w-screen h-screen justify-center"
          :class="
            scrollMode && wpsClient.media.isReflowable
              ? 'container-full-screen--scroll'
              : null
          "
        >
          <div
            id="content"
            :class="[
              wpsClient.media.isPDF || wpsClient.media.isFXL
                ? 'container-main-content--full'
                : null,
              !wpsClient.media.isPDF ? 'container-main-content--hidden' : null,
            ]"
            class="container-main-content relative justify-center flex w-full"
          >
            <Transition v-if="wpsClient.token.uiEnabled" name="fade-button">
              <button
                v-if="
                  scrollMode && wpsClient.media.isReflowable /* &&
                  navigator()?.currentPositionNumbers[0] > 1 */
                "
                class="go-to-previous-section floating-button floating-button__left"
                @click="goTo('previous')"
              >
                {{ t("reader.buttonGoPrevious") }}
              </button>
            </Transition>
            <NavigatorComponent
              v-if="visibilityStore.navigatorReady && !devtoolsOpened"
              ref="navRef"
              :initial-position="
                progressionStore.lastKnownPosition || initialPosition
              "
              :go-to="goTo"
              @ready="ready"
              @error="handleNavigatorError"
            />

            <SecondaryMenu
              v-if="wpsClient.token.uiEnabled && navigatorRef?.activeSelection"
              :selection="navigatorRef.activeSelection"
            />

            <DictionaryModal
              v-if="wpsClient.token.uiEnabled && activeDefinition"
              :definition="activeDefinition"
            />
            <Transition v-if="wpsClient.token.uiEnabled" name="fade-button">
              <button
                v-if="
                  scrollMode && wpsClient.media.isReflowable /* &&
                  navigator()?.currentPositionNumbers[0] <
                    wpsClient.positions.length */
                "
                class="go-to-next-section floating-button floating-button__right"
                @click="goTo('next', false)"
              >
                {{ t("reader.buttonGoNext") }}
              </button>
            </Transition>
            <div
              v-if="
                wpsClient.token.uiEnabled &&
                (!visibilityStore.showUI ||
                  (wpsClient.media.isPDF && scrollMode))
              "
              class="position w-full absolute text-center right-0 pb-1.5"
              :class="[
                wpsClient.media.isPDF ? 'position--full' : null,
                scrollMode ? 'position--scroll' : null,
              ]"
              @click="
                wpsClient.interactive ? null : visibilityStore.setShowUI(true)
              "
            >
              <span class="sr-only"> {{ t("reader.currentPosition") }}</span>
              <span class="position__text">
                {{ navigatorRef?.positionIndicator || "1" }}
                {{ t("readerProgressionBar.positionFragment") }}
                {{ navigatorRef?.positions?.length || 1 }}
              </span>
            </div>
          </div>
        </article>

        <SideArrow
          v-if="wpsClient.token.uiEnabled && !scrollMode"
          side="right"
          :go-to="goTo"
        />

        <section
          class="footer-bar"
          :class="{
            ['footer-bar--vertical']: scrollMode,
            ['footer-bar--hidden']: !visibilityStore.showUI,
          }"
          @mouseover="
            attributesStore.screenHasHover &&
            !wpsClient.interactive &&
            !textSelected()
              ? visibilityStore.setShowUI(true)
              : null
          "
        >
          <ReaderProgressionBar
            v-if="wpsClient.token.uiEnabled && visibilityStore.navigatorReady"
            :vertical-mode="scrollMode"
          />

          <div
            class="actions-container"
            :class="{ 'actions-container--vertical': scrollMode }"
          >
            <TTSPlayer
              v-if="mediaPlayer"
              class="tts-container"
              :class="{ 'tts-container--overlay': interactiveCtaOpen }"
              @click="closeInteractiveCta?.()"
            />

            <ZoomSelector
              v-if="
                !attributesStore.isMobile &&
                !wpsClient.interactive &&
                !wpsClient.media.isMediaOverlay
              "
              :vertical-mode="scrollMode"
              :allow-button="
                !scrollMode && (wpsClient.media.isPDF || wpsClient.media.isFXL)
              "
            />

            <CursorTool
              v-if="wpsClient.media.isPDF && !attributesStore.isMobile"
              :is-pannable="isPannable"
            />

            <div
              v-if="visibilityStore.showZoomCard && !attributesStore.isMobile"
              class="overlay overlay--transparent"
              @click="visibilityStore.setShowZoomCard(false)"
            ></div>

            <ZoomCard
              v-if="
                !attributesStore.isMobile &&
                !wpsClient.interactive &&
                !wpsClient.media.isMediaOverlay
              "
              :vertical-mode="scrollMode"
              :floating-mode="true"
            />
          </div>
        </section>
      </main>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;

.reader-container {
  max-height: var(--safe-height);
  max-width: var(--safe-width);
  margin-top: var(--safe-area-t);
  margin-bottom: var(--safe-area-b);
  margin-left: var(--safe-area-l);
  margin-right: var(--safe-area-r);
  &--interactiveMode {
    .overlay {
      z-index: 2;
    }
  }
}
.container-full-screen {
  height: 100%;
  width: 100%;
  &--scroll {
    height: calc(100% - $scrollBottomPadding);
  }
}

.container-main-content {
  margin: $barHeight 0;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;

  &--full {
    margin: 0;
    &.scroll-screen {
      max-width: none;
    }
  }

  &--hidden {
    opacity: 0;
    pointer-events: none;
  }
  &.scroll-screen {
    max-width: $screenSM;
  }

  .position {
    bottom: -$barHeight;
    color: var(--DM__theme-shadow-text-color);
    &--full {
      bottom: 0;
      color: var(--DM__theme-font-color);
      z-index: 2;
      .position__text {
        border: 1px solid;
        border-radius: 65px;
        padding: 4px $spacingM 3px;
        background: var(--DM__theme-fill-color);
        opacity: 0.8;
      }
    }
    &--scroll {
      visibility: hidden;
    }
  }
}

.topbar--shadow {
  color: var(--DM__theme-shadow-text-color);
  z-index: 1;

  &-fxl {
    & > .truncate {
      // Hide title text in FXL publications
      opacity: 0;
    }
  }

  &-pdf {
    z-index: 3;

    .square-icon-button--shadow {
      &::before {
        content: "";
        position: absolute;
        background: var(--DM__theme-hover-color);
        border-radius: 50%;
        width: 30px;
        height: 30px;
        opacity: 0.7;
        z-index: -1;
      }
    }
  }

  &-interactive {
    user-select: none;
  }

  &-no-hover {
    z-index: 3;
  }
  .square-icon-button--shadow {
    //Adding 2px to match position (border on visible UI)
    top: $spacingM + 2px;
    right: 0;

    @media (min-width: $screenM) {
      right: $spacingM;
    }
  }
  .square-icon-button--shadow-bg {
    @media (max-width: $screenM) {
      &::before {
        content: "";
        position: absolute;
        background: var(--DM__theme-hover-color);
        border-radius: 50%;
        width: 30px;
        height: 30px;
        opacity: 0.7;
        z-index: -1;
      }
    }
  }
}
.go-to-next-section,
.go-to-previous-section {
  bottom: calc(-1 * ($scrollBottomPadding) + 0.5rem);
}
.floating-button {
  position: absolute;
  display: flex;
  align-items: center;
  color: var(--DM__theme-font-color);
  padding: 0 $spacingM;
  border-radius: $spacingS;
  border: $spacingXS solid transparent;
  z-index: 1;
  &:focus {
    outline: var(--DM__theme-border-color);
    border-color: var(--DM__theme-border-color);
  }
  &:focus:not(:focus-visible) {
    outline: 0;
    border-color: transparent;
  }
  &::before {
    font-size: 1.2rem;
    font-family: "Material Icons";
    transition: all 0.3s ease-in-out;
    font-feature-settings: "liga";
    -webkit-font-feature-settings: "liga";
  }
  &::after {
    content: " ";
    width: 0;
    position: absolute;
    bottom: 0;
    transition: all 0.4s ease-in-out;
    height: $spacingXS;
    background-color: var(--DM__theme-font-color);
  }
  &__left {
    left: max(0px, (50% - (var(--iframe-width) / 2)));
    &::before {
      content: "arrow_back";
      padding-right: $spacingXS;
    }
    &::after {
      left: 50px;
    }
    @media (hover: hover) {
      &:hover {
        &::before {
          padding-right: 8px;
        }
        &::after {
          width: calc(100% - 75px);
        }
      }
    }
  }
  &__right {
    right: max($barHeight, (50% - (var(--iframe-width) / 2)));
    display: flex;
    flex-direction: row-reverse;
    &::before {
      content: "arrow_forward";
      padding-left: $spacingXS;
    }
    &::after {
      right: 50px;
    }
    @media (hover: hover) {
      &:hover {
        &::before {
          padding-left: 8px;
        }
        &::after {
          width: calc(100% - 75px);
        }
      }
    }
  }
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  background-color: var(--DM__theme-fill-color);
  gap: $spacingM;
  padding: 0 $spacingM;
  flex-direction: column-reverse;

  @media (min-width: $screenSM) {
    flex-direction: row;
  }

  &--vertical {
    right: 0;
    left: auto;
    flex-direction: column;
    width: $barHeight;
    height: calc(100% - $barHeight);
    .bottombar {
      flex-direction: column;
      height: 100%;
      width: auto;
    }
  }
  &--hidden {
    opacity: 0;
    transition: opacity 0.8s cubic-bezier(0, 1, 0.5, 1);
  }
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacingM;

  .tts-container {
    z-index: 1;
  }

  &--vertical {
    position: absolute;
    flex-wrap: wrap-reverse;
    gap: $spacingS;
    justify-content: flex-end;
    right: $barHeight;
    padding-right: $spacingM;
    bottom: 0;
    height: auto;

    .cursor-tool {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      :deep(.cursor-tool__button) {
        width: $roundedButtonSize;
        height: $roundedButtonSize;
        border: 2px solid var(--DM__color-grayBase);
        border-radius: 50%;
      }
    }

    .tts-container {
      height: auto;
      width: auto;
      bottom: 0;
      max-width: calc($screenXS - $barHeight);
      border: 2px solid var(--DM__color-grayBase);
      border-radius: $spacingL $spacingL 0 0;
      padding: $spacingS $spacingL 0;
      position: fixed;
      left: calc(50% - ($barHeight / 2));
      transform: translateX(-50%);

      @media (min-width: $screenSM) {
        max-width: none;
        bottom: 0;
        transform: none;
        position: relative;
        left: auto;
        transform: none;
      }
    }

    .zoom-selector {
      border-radius: $spacingM;
    }

    .zoom-selector__button-zoom {
      background-color: var(--DM__theme-fill-color);
    }
  }
}
</style>
